import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnInit,
  Signal,
  signal,
  ViewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  exhaustMap,
  filter,
  finalize,
  first,
  forkJoin,
  map,
  Observable,
  of,
  skip,
  skipWhile,
  Subscription,
  switchMap,
  tap,
  throttleTime,
} from 'rxjs';

import { runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  CanvasElementDataInputPayload,
  PropsOf,
  RuntimeEventQueryData,
  TagColorInfo,
} from '@blaze-runtime/engine-domain';
import { markEventAsHandled } from '@blaze-runtime/engine-util';
import {
  ComparisonOperator,
  LogicalPredicateNode,
  LogicalQueryNode,
  LogicalType,
  PredicateType,
  QueryNodeType,
  UserInfo,
} from '@blaze-shared/api-contracts-domain';
import { BlazeAccountFacade } from '@blaze-shared/app-state-data-access';
import { DatasetsApiService, SubscriptionAction } from '@blaze-shared/datasets-data-access';
import {
  DataBindingSourceInfo,
  ElementLabelProps,
  ElementQueryState,
  getActiveBinding,
  ItemContainerActionsIds,
  ITEMS_CONTAINER_ACTION_ADD_CONVERSATION,
  ITEMS_CONTAINER_ACTION_EDIT_CONVERSATION,
  ITEMS_CONTAINER_ACTION_LEAVE_CONVERSATION,
  ITEMS_CONTAINER_ACTION_SEND_MESSAGE,
  makeContainerVisualEffectStyles,
  PaginatedComponentModel,
  PaginationDirection,
  type PropertiesConfigInputColorModel,
} from '@blaze-shared/elements-domain';
import { DatasetRowsService } from '@blaze-shared/schema-data-access';
import { findColumn } from '@blaze-shared/schema-domain';
import {
  BlazeSchema,
  convertBlazeSchemaToElementSchema,
  DatasetRow,
  type ElementSchema,
} from '@blaze-shared/schema-domain';
import { DialogService, SharedModule } from '@blaze-shared/shared-feature';
import {
  deepClone,
  getFilenameFromUrl,
  isDeepEqual,
  isEqual,
  useEmitTracker,
} from '@blaze-shared/shared-util';
import { ThemesService } from '@blaze-shared/theming-data-access';
import { FALLBACK_THEME } from '@blaze-shared/theming-domain';
import { FileResult, UploaderFacade } from '@blaze-shared/uploader';
import {
  findFirstNodeInWorkflowOfType,
  GetWorkflowNode,
  isGetNode,
} from '@blaze-shared/workflow-domain';

import { ElementHeaderComponent } from '../../../components/element-header/element-header.component';
import { ContainerActionsService } from '../../../services/container-actions.service';
import { ElementPaginationService } from '../../../services/element-pagination.service';
import { SubscriptionService } from '../../../services/subscription.service';
import { CanvasElementBaseComponent } from '../../__base/base.component';
import { CHAT_DEFAULTS, type ChatMessageAlignment, ChatPropsModel } from '../chat-element.models';
import { ChatEmptyStateComponent } from '../chat-empty-state/chat-empty-state.component';
import { ChatFormComponent } from '../chat-form/chat-form.component';
import { ChatTextInputComponent } from '../chat-text-input/chat-text-input.component';
import {
  ChatConversation,
  ChatConversationViewModel,
  ChatLastReadMessage,
  ChatMember,
  ChatMemberInfo,
  ChatMessage,
  ChatMessageId,
  ChatSearchResult,
} from '../chat.models';
import {
  ConversationComponent,
  LoadMoreMessagesEvent,
} from '../conversation/conversation.component';
import {
  type ConversationsFilter,
  ConversationsFiltersComponent,
} from '../conversations-filters/conversations-filters.component';
import { ConversationsListComponent } from '../conversations-list/conversations-list.component';
import { ConversationsSearchComponent } from '../conversations-search/conversations-search.component';
import { ConversationsSearchResultsComponent } from '../conversations-search-results/conversations-search-results.component';
import { filterConversations } from '../utils/conversations-filter.utils';

import { ChatElementService } from './chat-element.service';
import { ChatSchemaService } from './chat-schema.service';
import { ChatStore } from './chat.store';

const MESSAGES_REFRESH_THROTTLE_TIME_MS = 100;
const CONVERSATIONS_REFRESH_THROTTLE_TIME_MS = 100;
const MEMBERS_STATUS_REFRESH_THROTTLE_TIME_MS = 1000;
const MESSAGES_PAGINATION_PAGE_SIZE = 20;

const TEXT_INPUT_PLACEHOLDER_PREFIX = 'Message ';

@UntilDestroy()
@Component({
  selector: 'be-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  imports: [
    ChatTextInputComponent,
    ConversationComponent,
    ConversationsFiltersComponent,
    ConversationsListComponent,
    ConversationsSearchComponent,
    ConversationsSearchResultsComponent,
    MatIconModule,
    ChatFormComponent,
    SharedModule,
    ElementHeaderComponent,
    CommonModule,
    ChatEmptyStateComponent,
  ],
  providers: [ChatElementService, ChatStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatComponent extends CanvasElementBaseComponent<'chat'> implements OnChanges, OnInit {
  private readonly blazeAccountFacade = inject(BlazeAccountFacade);

  private readonly uploaderFacade = inject(UploaderFacade);
  private readonly dialogService = inject(DialogService);
  private readonly datasetsApiService = inject(DatasetsApiService);
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly chatElementService = inject(ChatElementService);
  private readonly chatSchemaService = inject(ChatSchemaService);
  private readonly subscriptionService = inject(SubscriptionService);
  private readonly containerActionsService = inject(ContainerActionsService);
  private readonly elementPaginationService = inject(ElementPaginationService);
  private readonly themeService = inject(ThemesService);
  private readonly chatMessageEditService = inject(ChatStore);

  @Input() queryState$?: Observable<ElementQueryState | undefined>;
  @Input() paginationPending$?: Observable<boolean>;

  @ViewChild(ChatTextInputComponent) textInput: ChatTextInputComponent | null = null;
  @ViewChild(ConversationComponent) conversation: ConversationComponent | null = null;

  @HostBinding('style.--chat-general-title') titleColor = FALLBACK_THEME.value.title;
  @HostBinding('style.--chat-general-background') backgroundColor = FALLBACK_THEME.value.canvas;
  @HostBinding('style.--chat-general-outline') outlineColor = FALLBACK_THEME.value.outline;
  @HostBinding('style.--chat-general-accent') accentColor = FALLBACK_THEME.value.accent;
  @HostBinding('style.--chat-general-accent-label') accentLabelColor =
    FALLBACK_THEME.value.accentLabel;
  @HostBinding('style.--chat-general-radius') radius = FALLBACK_THEME.value.radius;

  @HostBinding('style.--chat-participants-label') participantsLabelColor =
    FALLBACK_THEME.value.value;
  @HostBinding('style.--chat-participants-hover-background') participantsHoverBackgroundColor =
    FALLBACK_THEME.value.fill;
  @HostBinding('style.--chat-participants-selected-background')
  participantsSelectedBackgroundColor = `${FALLBACK_THEME.value.accent}0d`;

  @HostBinding('style.--chat-messages-my-text') messagesMyTextColor = FALLBACK_THEME.value.value;
  @HostBinding('style.--chat-messages-my-background') messagesMyBackgroundColor =
    FALLBACK_THEME.value.canvas;
  @HostBinding('style.--chat-messages-others-text') messagesOthersTextColor =
    FALLBACK_THEME.value.value;
  @HostBinding('style.--chat-messages-others-background') messagesOthersBackgroundColor =
    `${FALLBACK_THEME.value.accent}0d`;
  @HostBinding('style.--chat-messages-font-family') messagesFontFamily?: string;
  @HostBinding('style.--chat-messages-font-weight') messagesFontWeight?: string;
  @HostBinding('style.--chat-messages-font-size') messagesFontSize?: string;
  @HostBinding('style.--chat-messages-line-height') messagesLineHeight?: string;

  @HostBinding('style.--chat-input-text') inputTextColor = FALLBACK_THEME.value.value;
  @HostBinding('style.--chat-input-background') inputBackgroundColor?: string;
  @HostBinding('style.--chat-input-outline') inputOutlineColor?: string;
  @HostBinding('style.--chat-input-font-family') inputFontFamily?: string;
  @HostBinding('style.--chat-input-font-weight') inputFontWeight?: string;
  @HostBinding('style.--chat-input-font-size') inputFontSize?: string;
  @HostBinding('style.--chat-input-line-height') inputLineHeight?: string;

  @HostBinding('style.--chat-disabled-action-background') disabledActionBackgroundColor =
    FALLBACK_THEME.value.fill;
  @HostBinding('style.--chat-disabled-action-color') disabledActionColor =
    FALLBACK_THEME.value.fill;

  @HostBinding('style.box-shadow') boxShadow = 'none';

  private conversationsMap = new Map<number, DatasetRow>();
  private conversationsSortWeightsInitialized = false;
  private readonly introConversationIds = signal(new Set<number>());

  private readonly chatMembers$: Observable<ChatMember[]>;
  private readonly chatConversations$: Observable<ChatConversation[]>;
  private readonly selectedConversationId$: Observable<number | undefined>;
  private readonly messagesDataInput = this.getDataInput('messages');
  private readonly conversationsDataInput = this.getDataInput('conversations');
  private readonly conversationsInfoDataInput = this.getDataInput('conversationsInfo');
  private readonly membersDataInput = this.getDataInput('members');
  private readonly outputEmitTracker = useEmitTracker();
  private readonly paginationEmitTracker = useEmitTracker();
  private paginationProps: PaginatedComponentModel = {
    state: {
      hasPreviousPage: false,
      hasNextPage: false,
    },
    countOptions: [],
    isServerSide: true,
    enabled: true,
    pageIndex: 0,
    pageSize: MESSAGES_PAGINATION_PAGE_SIZE,
    backwardPaginationSupported: false,
  };
  private messagesChangesSubscription = Subscription.EMPTY;

  protected readonly createdConversationId = signal<number | null>(null);

  protected readonly isViewAsMode = toSignal(this.projectContextFacade.isPreviewOfAnotherUser$, {
    initialValue: false,
  });
  protected readonly viewAsLabel = toSignal(
    this.projectContextFacade.varUser$.pipe(
      map((varUser) => {
        const firstName = varUser?.FirstName?.trim();
        const lastName = varUser?.LastName?.trim();
        if (firstName && lastName) {
          return `${firstName} ${lastName}`;
        }
        if (firstName || lastName) {
          return firstName || lastName;
        }
        return varUser?.Email;
      }),
    ),
  );
  protected originalSchemas: {
    messages: BlazeSchema;
    conversations: BlazeSchema;
    conversationsInfo: BlazeSchema;
    members: BlazeSchema;
  } | null = null;
  protected readonly markEventAsHandled = markEventAsHandled;

  chatSchemasLoaded = signal(false);
  membersTableName = signal<string>('');
  selectedConversationId = signal<number | undefined>(undefined);
  chatConversations = signal<ChatConversation[]>([]);
  conversationsLoaded = signal(false);
  conversationsBound = signal(false);
  allMessages = signal<Record<number, Map<ChatMessageId, ChatMessage>>>({});
  chatMembers = signal<ChatMember[]>([]);
  newChatMembers = signal<ChatMember[]>([]);
  colorsMap = signal<Record<string, TagColorInfo>>({});
  currentUser = signal<UserInfo | undefined>(undefined);

  attachments = signal<FileResult[]>([]);

  allConversationsUnreadMessagesCount = signal<Map<number, number>>(new Map());
  allConversationsLastMessageIds = signal<Map<number, number>>(new Map());
  activeConversationsFilter = signal<ConversationsFilter>('all');

  isTextInputDisabled = computed(() => !this.selectedConversationId());
  isSendDisabled = signal(true);
  shouldAutoSelectConversation = computed(() => !this.showConversationsMenu());

  title = signal<ElementLabelProps>(CHAT_DEFAULTS.content.title);
  introMessagesConfig = signal<ChatPropsModel['content']['intro'] | undefined>(undefined);
  showConversationsMenu = signal<boolean>(CHAT_DEFAULTS.content.showConversationsMenu);
  showConversationFilters = signal<boolean>(CHAT_DEFAULTS.content.showConversationFilters);
  addConversationEnabled: Signal<boolean>;
  editConversationEnabled: Signal<boolean>;
  leaveConversationEnabled: Signal<boolean>;
  enabledConversationMenu = signal<boolean>(CHAT_DEFAULTS.content.showConversationsMenu);
  enableMessageEdit = signal<boolean>(CHAT_DEFAULTS.content.enableMessageEdit);
  messageAlignment = signal<ChatMessageAlignment>(CHAT_DEFAULTS.content.messageAlignment);

  /**
   * string is for message id
   * 'top'/'bottom' for scroll position.
   */
  scrollTo = signal<'top' | 'bottom' | ChatMessageId | undefined>('bottom');
  allMessagesLoaded = signal(false);

  showChatForm = signal(false);
  isLoading = signal(false);
  isConversationListLoading = computed(
    () =>
      (this.conversationsBound() && !this.conversationsLoaded()) ||
      this.createdConversationId() !== null,
  );
  showLoadingIndicator = computed(() => this.isLoading() || this.isConversationListLoading());

  chatMembersStatuses = computed(
    () =>
      new Map(
        this.chatMembersWithoutCurrentUser()
          .filter(
            (member): member is typeof member & { email: string } =>
              !!(member.email && (member.status || member.statusDescription)),
          )
          .map((member) => [member.email, member]),
      ),
  );
  statusColorMap = signal<Record<string, string>>({});
  conversationsSortWeights = signal<Map<number, number>>(new Map());

  chatConversationsVM = computed((): ChatConversationViewModel[] => {
    const statuses = this.chatMembersStatuses();
    const sortPriorities = this.conversationsSortWeights();
    return this.chatConversations()
      .map((conversation) => {
        const membersStatuses: ChatMemberInfo[] = [];
        for (const memberEmail of conversation.emails) {
          const emailStatus = statuses.get(memberEmail);
          if (emailStatus) {
            membersStatuses.push(emailStatus);
          }
        }

        return {
          ...conversation,
          membersStatuses,
        };
      })
      .sort((a, b) => {
        const sortWeightA = sortPriorities.get(a.id) ?? 0;
        const sortWeightB = sortPriorities.get(b.id) ?? 0;

        if (sortWeightA !== sortWeightB) {
          return sortWeightB - sortWeightA;
        }

        return (a.name ?? '').localeCompare(b.name ?? '');
      });
  });

  filteredChatConversationsVM = computed((): ChatConversationViewModel[] =>
    filterConversations(
      this.chatConversationsVM(),
      this.activeConversationsFilter(),
      this.allConversationsUnreadMessagesCount(),
      this.selectedConversationId(),
    ),
  );

  selectedConversation = computed<ChatConversationViewModel | undefined>(() => {
    const conversationId = this.selectedConversationId();
    return conversationId
      ? this.chatConversationsVM().find((c) => c.id === conversationId)
      : undefined;
  });

  selectedConversationCreatedBy = computed<ChatMember | undefined>(() => {
    const selectedConversation = this.selectedConversation();

    if (!selectedConversation) {
      return undefined;
    }

    return this.chatElementService.getMemberByConversation(
      selectedConversation.createdBy,
      this.chatMembers(),
    );
  });

  chatMessages = computed<ChatMessage[]>(
    () => {
      const allMessages = this.allMessages();
      const conversationId = this.selectedConversationId();

      if (!conversationId) {
        return [];
      }

      const realMessages = allMessages[conversationId]
        ? Array.from(allMessages[conversationId].values())
        : [];

      if (this.introConversationIds().has(conversationId)) {
        const introMessages = this.generateInitialMessages(conversationId) ?? [];
        return this.sortMessages([...introMessages, ...realMessages]);
      }

      return this.sortMessages(realMessages);
    },
    {
      equal: isDeepEqual,
    },
  );
  chatIntroImage = computed<string | undefined>(() => {
    const imgConfig = this.introMessagesConfig();
    const conversationId = this.selectedConversationId();
    if (!conversationId || !imgConfig?.showImage) {
      return undefined;
    }

    if (!this.introConversationIds().has(conversationId)) {
      return undefined;
    }

    const hasRealMessages = (this.allMessages()[conversationId]?.size ?? 0) > 0;
    return hasRealMessages ? undefined : imgConfig.image || undefined;
  });

  unreadMessagesCount = computed<number>(() => {
    const ids = this.allConversationsUnreadMessagesCount();
    const conversationId = this.selectedConversationId();

    if (!conversationId) {
      return 0;
    }

    const unreadMessagesCount = ids.get(conversationId);

    return unreadMessagesCount ?? 0;
  });
  allMessagesRead = signal(false);
  lastReadMessageForConversation = signal<ChatLastReadMessage>({
    conversationId: undefined,
    lastReadMessageId: undefined,
  });

  chatMembersWithoutCurrentUser = computed(() =>
    this.newChatMembers()
      .filter((member) => member.email !== this.currentUser()?.email)
      .map((member) => ({
        ...member,
        statusColor: this.statusColorMap()[member.status ?? ''] || '',
      })),
  );

  showEmptyStateImage = signal<Map<number, string | undefined>>(new Map());

  currentMobileFocus = signal<'chat' | 'menu'>('chat');
  isMobileWidth = signal(true);
  showMenu = computed(() => {
    if (!this.enabledConversationMenu()) {
      return false;
    }

    return !this.isMobileWidth() || (this.isMobileWidth() && this.currentMobileFocus() === 'menu');
  });
  showConversation = computed(
    () => (this.isMobileWidth() && this.currentMobileFocus() === 'chat') || !this.isMobileWidth(),
  );
  showConversationList = signal(true);
  showSearchResults = signal(false);
  searchResultsMessages = signal<ChatSearchResult[]>([]);
  searchMessagesLoading = signal(false);
  searchResultsChats = signal<ChatConversation[]>([]);
  searchTerm = signal<string>('');
  pendingSearchNavigation = signal<ChatMessage | undefined>(undefined);
  loadMoreMessagesType = computed<'both' | 'top'>(() =>
    this.showSearchResults() ? 'both' : 'top',
  );

  placeholder = computed(() => {
    return `${TEXT_INPUT_PLACEHOLDER_PREFIX}${this.selectedConversation()?.name ?? ''}`;
  });

  constructor() {
    super();
    this.chatMembers$ = toObservable(this.chatMembers);
    this.chatConversations$ = toObservable(this.chatConversations);
    this.selectedConversationId$ = toObservable(this.selectedConversationId);

    this.projectContextFacade.userInfo$.pipe(untilDestroyed(this)).subscribe((user) => {
      this.currentUser.set(user);
    });

    this.getChatMembers()
      .pipe(untilDestroyed(this))
      .subscribe(({ all, create }) => {
        this.newChatMembers.set(create ?? all);
        this.chatMembers.set(all);
      });

    this.assignColors();

    this.viewportMode$.pipe(untilDestroyed(this)).subscribe((mode) => {
      this.isMobileWidth.set(mode === 'mobile');
    });

    effect(() => {
      const ids = this.allConversationsLastMessageIds();
      const conversationId = this.selectedConversationId();

      if (!conversationId) {
        this.lastReadMessageForConversation.set({
          conversationId: undefined,
          lastReadMessageId: undefined,
        });
        return;
      }

      if (this.lastReadMessageForConversation().conversationId === conversationId) {
        return;
      }
      const lastReadMessageId = ids.get(conversationId);
      if (this.lastReadMessageForConversation().lastReadMessageId === lastReadMessageId) {
        return;
      }
      this.lastReadMessageForConversation.set({ conversationId, lastReadMessageId });
    });

    const conversationsManagementEnabled = (props: PropsOf<'chat'>): boolean =>
      props.content.showConversationsMenu && props.content.conversationsManagementEnabled;
    this.addConversationEnabled = toSignal(
      this.visibilityComponentScope((props) => ({
        enabled: conversationsManagementEnabled(props),
        expression: props.content.actions.addConversation?.visibilityExpression,
      })),
      { initialValue: false },
    );
    this.editConversationEnabled = toSignal(
      this.visibilityComponentScope((props) => ({
        enabled: conversationsManagementEnabled(props),
        expression: props.content.actions.editConversation?.visibilityExpression,
      })),
      { initialValue: false },
    );
    this.leaveConversationEnabled = toSignal(
      this.visibilityComponentScope((props) => ({
        enabled: conversationsManagementEnabled(props),
        expression: props.content.actions.leaveConversation?.visibilityExpression,
      })),
      { initialValue: false },
    );
    effect(() => {
      this.selectedConversationId();
      this.chatMessageEditService.resetEditMessage(this.elementId);
    });

    this.initMarginHandler();
  }

  ngOnChanges(): void {
    if (!this.props?.content) {
      return;
    }

    if (this.props.content.title) {
      this.title.set(this.props.content.title);
    }

    this.introMessagesConfig.set(this.props.content.intro);

    this.showConversationsMenu.set(this.props.content.showConversationsMenu ?? false);
    this.showConversationFilters.set(this.props.content.showConversationFilters ?? true);
    this.enabledConversationMenu.set(this.props.content.showConversationsMenu ?? false);
    this.enableMessageEdit.set(this.props.content.enableMessageEdit ?? false);
    this.messageAlignment.set(this.props.content.messageAlignment ?? 'left');
    this.conversationsBound.set(!!this.bindingResults?.conversations);

    if (
      this.conversationsLoaded() &&
      !this.props.content.showConversationsMenu &&
      !this.showChatForm() &&
      !this.selectedConversationId()
    ) {
      this.onSelectedConversationChange(this.chatConversationsVM()[0]);
    }

    this.updateStyles();
  }

  ngOnInit(): void {
    if (this.enabledConversationMenu()) {
      this.currentMobileFocus.set('menu');
    }

    this.setSchemaForMessages();
    this.setSchemaForConversations();

    this.loadBlazeTableSchemas();
    this.cacheChatColumns();

    this.isPending$
      .pipe(
        first((pending) => !pending),
        switchMap(() =>
          combineLatest({
            conversations: this.loadConversations(),
            unreadMessagesData: this.loadUnreadMessagesData(),
          }),
        ),
      )
      .subscribe(({ conversations, unreadMessagesData }) => {
        const hadNoConversations = this.chatConversations().length === 0;
        const selectedConversationRemoved =
          !!this.selectedConversationId() &&
          !conversations.some((c) => c.id === this.selectedConversationId());

        const unreadMessagesDataEntries = Array.from(unreadMessagesData);

        const unreadMessagesCounts: Map<number, number> = new Map(
          unreadMessagesDataEntries.map(([conversationId, data]) => [
            conversationId,
            data.unreadMessages,
          ]),
        );

        this.updateConversationsSortWeights(unreadMessagesDataEntries);

        this.allConversationsUnreadMessagesCount.set(unreadMessagesCounts);
        this.allConversationsLastMessageIds.set(
          new Map(
            unreadMessagesDataEntries.map(([conversationId, data]) => [
              conversationId,
              data.lastMessageId,
            ]),
          ),
        );

        this.chatConversations.set(conversations);
        this.conversationsLoaded.set(true);

        const createdConversationId = this.createdConversationId();
        if (createdConversationId) {
          this.onSelectedConversationChange(
            conversations.find((c) => c.id === createdConversationId),
          );
          this.createdConversationId.set(null);
          return;
        }

        if (this.shouldAutoSelectConversation()) {
          if (hadNoConversations || selectedConversationRemoved) {
            this.onSelectedConversationChange(this.chatConversationsVM()[0]);
          }
        } else if (selectedConversationRemoved) {
          this.onSelectedConversationChange(undefined);
        }
      });

    this.loadMessages()
      .pipe(untilDestroyed(this))
      .subscribe((messages) => {
        this.upsertMessages(messages);
        this.conversation?.closeSwipeableElements(true);
      });

    this.listenToDataInputBindingQueryChanges('conversations')
      .pipe(untilDestroyed(this))
      .subscribe((query) => {
        this.listenToConversationsChanges(query);
      });

    this.listenToQueryStateChanges();

    this.listenToDataInputBindingQueryChanges('members')
      .pipe(untilDestroyed(this))
      .subscribe((query) => {
        this.listenToChatMembersStatusChanges(query);
      });

    this.element$
      .pipe(
        switchMap((element) => this.chatMessageEditService.getEditMessage(element.id)),
        untilDestroyed(this),
      )
      .subscribe((message) => {
        this.attachments.set(
          message?.attachments?.map((attachment): FileResult => {
            return {
              filename: getFilenameFromUrl(attachment),
              url: attachment,
            };
          }) || [],
        );
      });
  }

  markMessageAsRead(lastMessageId: number): void {
    if (this.isViewAsMode()) {
      return;
    }

    const conversation = this.selectedConversation();
    if (!conversation) {
      return;
    }
    const conversationId = conversation.id;
    const currentUser = this.currentUser();

    if (!conversationId || !currentUser) {
      return;
    }

    const messagesMap = this.allMessages()[conversationId];
    if (!messagesMap.size) {
      return;
    }

    const datasetRow = this.conversationsMap.get(conversationId);
    if (!datasetRow) {
      return;
    }

    const unreadMessagesCount = this.allConversationsUnreadMessagesCount().get(conversationId);

    if (
      unreadMessagesCount === undefined &&
      this.allConversationsLastMessageIds().get(conversationId) === lastMessageId
    ) {
      return;
    }

    this.allConversationsUnreadMessagesCount.update((messageIds) => {
      return new Map(messageIds).set(conversationId, 0);
    });

    this.assertSchemas();
    const conversationsInfoColumns = this.chatElementService.getConversationsInfoColumns();

    this.datasetRowsService
      .update(
        this.originalSchemas.conversationsInfo.id,
        {
          value: { [conversationsInfoColumns.lastMessageId]: lastMessageId },
          queryNode: {
            nodeType: PredicateType.Logical,
            logicalType: LogicalType.And,
            children: [
              {
                nodeType: PredicateType.Comparison,
                names: [conversationsInfoColumns.conversation],
                value: datasetRow[this.chatElementService.getConversationsColumns().rowId],
              },
              {
                nodeType: PredicateType.Comparison,
                names: [conversationsInfoColumns.membersEmail],
                value: currentUser.email,
              },
            ],
          },
        },
        { omitCatchError: true },
      )
      .pipe(first())
      .subscribe();
  }

  onAdd(): void {
    if (this.isViewAsMode()) {
      return;
    }
    this.selectedConversationId.set(undefined);
    this.isSendDisabled.set(true);
    this.showChatForm.set(true);
    this.openChat();
  }

  onEdit(): void {
    if (this.isViewAsMode()) {
      return;
    }
    this.showChatForm.set(true);
  }

  onExit(): void {
    if (this.isViewAsMode()) {
      return;
    }

    const conversationId = this.selectedConversationId();
    const currentUser = this.currentUser();
    if (!conversationId || !currentUser) {
      return;
    }

    this.dialogService
      .openConfirmDialog({
        title: 'Exit Conversation',
        description: 'Are you sure you want to exit this conversation?',
        confirmLabel: 'Yes, exit',
        cancelLabel: 'Cancel',
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        tap(() => this.isLoading.set(true)),
        switchMap(() => this.removeUserFromConversation(conversationId, currentUser.email)),
        switchMap(() => {
          return this.datasetRowsService.get({
            datasetId: this.originalSchemas?.conversations.id,
            filterQuery: {
              nodeType: QueryNodeType.Logical,
              logicalType: LogicalType.And,
              children: [
                {
                  column: this.chatElementService.getConversationsColumns().id,
                  displayInfos: [],
                  isFormulaValue: false,
                  isOptional: false,
                  nodeType: QueryNodeType.Comparison,
                  operator: ComparisonOperator.Eq,
                  value: `${conversationId}`,
                },
              ],
            },
          });
        }),
      )
      .subscribe((datasetRow: DatasetRow) => {
        this.isLoading.set(false);
        this.selectedConversationId.set(undefined);
        this.isSendDisabled.set(true);
        this.handleConversationLeftAction(datasetRow[0]);
      });
  }

  onSelectedConversationChange(conversation: ChatConversation | undefined): void {
    this.conversation?.closeSwipeableElements(true);
    this.store.dispatch(
      runtimeActions.updateElementQueryNode({ elementId: this.elementId, query: undefined }),
    );
    this.selectedConversationId.set(conversation?.id);
    this.isSendDisabled.set(!conversation);
    this.showChatForm.set(false);

    if (this.textInput) {
      this.textInput.resetTextInput(false);
    }

    const schema = this.conversationsDataInput.schema;

    if (!schema) {
      return;
    }

    const data = conversation ? this.conversationsMap.get(conversation.id) : undefined;

    this.openChat();

    if (!this.showSearchResults()) {
      this.allMessagesRead.set(
        !this.allConversationsUnreadMessagesCount().get(conversation?.id ?? -1),
      );
      this.scrollTo.set('bottom');
    }

    if (this.outputEmitTracker.shouldEmit(data)) {
      this.output.emit({
        eventType: 'chat-conversation-select',
        schema: convertBlazeSchemaToElementSchema(
          schema,
          this.elementId,
          'chat-conversation-select',
        ),
        data,
        isInitialData: false,
      });
    }
  }

  uploadAttachments(files: File[]): void {
    this.isSendDisabled.set(true);

    const attachmentColName = this.chatElementService.getMessagesColumns().attachments;
    const acceptedFileTypes =
      findColumn(this.originalSchemas?.messages.columns, attachmentColName)?.columnType === 'Image'
        ? this.uploaderFacade.supportedImageTypes
        : ['*.*'];

    this.uploaderFacade
      .upload({
        accountId: this.blazeAccountFacade.getAccountId(),
        preselectFiles: files,
        isMultipleSelection: true,
        acceptedFileTypes,
      })
      .subscribe((result) => {
        if (result) {
          this.attachments.update((attachments) => [...attachments, ...result]);
        }
        this.isSendDisabled.set(false);
      });
  }

  deleteAttachment(attachment: FileResult): void {
    this.attachments.update((attachments) => attachments.filter((a) => a !== attachment));
  }

  confirmSending(message: string): void {
    let sendConfirmation$: Observable<boolean | undefined> = of(true);
    if (this.isViewAsMode()) {
      sendConfirmation$ = this.dialogService
        .openConfirmDialog({
          title: 'Sending as yourself',
          description:
            'You are currently viewing as another user. Messages will be sent from your account.',
          confirmLabel: 'Send',
          cancelLabel: 'Cancel',
        })
        .afterClosed();
    }

    sendConfirmation$.pipe(untilDestroyed(this)).subscribe((confirmed) => {
      if (confirmed) {
        this.sendMessage(message);
      }
    });
  }

  updateMessage(chatMessage: ChatMessage): void {
    const message = chatMessage.content;
    this.assertSchemas();

    const selectedConversationId = this.selectedConversationId();
    const isMessageEmpty = !message?.length && !this.attachments().length;

    if (this.isSendDisabled() || !selectedConversationId || isMessageEmpty) {
      return;
    }

    const selectedConversationDatasetRow = this.conversationsMap.get(selectedConversationId);

    if (!selectedConversationDatasetRow) {
      return;
    }

    const messagesColumns = this.chatElementService.getMessagesColumns();

    const datasetRow: DatasetRow = {
      [messagesColumns.id]: chatMessage.id,
      [messagesColumns.content]: message ?? '',
      [messagesColumns.attachments]: this.attachments()
        .map((file) => file.url)
        .filter(Boolean),
      [messagesColumns.modifiedAt]: new Date().toISOString(),
    };
    const optimisticMessage: ChatMessage = {
      ...chatMessage,
      content: message,
      attachments: this.attachments()
        .map((file) => file.url)
        .filter(Boolean) as string[],
      updatedAt: new Date(),
    };
    this.messageOptimisticSave(optimisticMessage, true);

    this.datasetsApiService
      .postUpdate(this.originalSchemas.messages.id, {
        value: datasetRow,
        query: [
          {
            name: messagesColumns.id,
            names: [messagesColumns.id],
            value: chatMessage.id,
            op: ComparisonOperator.Eq,
          },
        ],
      })
      .pipe(
        finalize(() => {
          this.chatMessageEditService.setEditMessage({
            chatId: this.elementId,
            message: undefined,
          });
        }),
      )
      .subscribe({
        next: () => {
          this.attachments.set([]);
        },
        error: () => {
          if (optimisticMessage?.id) {
            this.setErrorForTempMessage(selectedConversationId, optimisticMessage.id);
          }
        },
      });
  }

  saveConversation({ name, members }: { name: string; members: ChatMember[] }): void {
    if (this.isViewAsMode()) {
      return;
    }

    this.assertSchemas();
    this.isLoading.set(true);

    const selectedConversationId = this.selectedConversationId();

    if (selectedConversationId) {
      this.updateConversation(selectedConversationId, name, members);
    } else {
      this.createConversation(name, members);
    }
  }

  closeConversationForm(): void {
    let conversation = this.selectedConversation();
    if (!conversation) {
      conversation = this.shouldAutoSelectConversation()
        ? this.chatConversationsVM()[0]
        : undefined;
    }
    this.onSelectedConversationChange(conversation);
  }

  loadMoreMessages({ lastMessage, operator }: LoadMoreMessagesEvent): void {
    operator ??= ComparisonOperator.Lt;

    const size = Math.max(this.unreadMessagesCount(), this.paginationProps.pageSize);
    const data: RuntimeEventQueryData = {
      sort: {
        columnName: this.schemaService.getColumnName(
          this.props?.content.input?.messages.createdTime,
        ),
        direction: operator === ComparisonOperator.Lt ? 'Descending' : 'Ascending',
      },
      page: {
        size,
        direction: PaginationDirection.Forwards,
        index: 0,
      },
      query: lastMessage
        ? {
            nodeType: QueryNodeType.Logical,
            logicalType: LogicalType.And,
            children: [
              {
                nodeType: QueryNodeType.Comparison,
                value: lastMessage.id.toString() ?? '',
                operator,
                displayInfos: [],
                isOptional: false,
                isFormulaValue: false,
                column: this.chatElementService.getMessagesColumns().id,
              },
            ],
          }
        : undefined,
      elementId: this.elementId,
      dataSource: this.props?.data?.find((data) => data?.key === 'messages')?.dataSource,
    };

    if (!this.paginationEmitTracker.shouldEmit(data)) {
      return;
    }

    this.output.emit({
      eventType: 'query',
      data,
      isInitialData: false,
      output: 'pagination',
    });

    this.store.dispatch(
      runtimeActions.updateElementQueryNode({ elementId: this.elementId, query: undefined }),
    );
    if (!this.showSearchResults()) {
      this.scrollTo.set(undefined);
    }
  }

  openMenu(): void {
    this.currentMobileFocus.set('menu');
  }

  openChat(): void {
    this.currentMobileFocus.set('chat');
  }

  onConversationsSearch(search: string): void {
    if (!search) {
      this.clearSearch();
      return;
    }
    this.showConversationList.set(false);
    this.showSearchResults.set(true);
    this.searchResultsMessages.set([]);
    this.searchMessagesLoading.set(true);
    const lowerCaseSearch = search.toLowerCase();
    this.searchResultsChats.set(
      this.chatConversations().filter((c) => c.displayName.toLowerCase().includes(lowerCaseSearch)),
    );
    this.searchTerm.set(search);

    this.getSearchResults(search)
      .pipe(
        finalize(() => this.searchMessagesLoading.set(false)),
        untilDestroyed(this),
      )
      .subscribe((results) => {
        const currentYear = new Date().getFullYear();
        let conversationTemporaryCount = 1;
        const messagesCountByMessageId: [ChatMessage['id'], number][] = [];
        const newSearchResults: ChatSearchResult[] = results.reverse().map((result, i, arr) => {
          const isSameConversationAsPrevious = arr[i - 1]?.conversationId === result.conversationId;
          if (isSameConversationAsPrevious) {
            conversationTemporaryCount++;
          } else {
            if (messagesCountByMessageId[messagesCountByMessageId.length - 1]) {
              messagesCountByMessageId[messagesCountByMessageId.length - 1][1] =
                conversationTemporaryCount;
            }
            conversationTemporaryCount = 1;
            messagesCountByMessageId.push([result.id, 1]);
          }
          if (i === arr.length - 1) {
            messagesCountByMessageId[messagesCountByMessageId.length - 1][1] =
              conversationTemporaryCount;
          }

          const resultConversation = !isSameConversationAsPrevious
            ? this.chatConversations().find((c) => c.id === result.conversationId)
            : undefined;
          const conversation = resultConversation
            ? {
                displayName: resultConversation.displayName,
                image: resultConversation.image,
              }
            : undefined;

          const colorInfo = !isSameConversationAsPrevious
            ? this.colorsMap()[result.conversationId]
            : undefined;

          return {
            result,
            isCurrentYear: new Date(result.createdAt).getFullYear() === currentYear,
            conversation,
            colorInfo,
          };
        });

        messagesCountByMessageId.forEach(([messageId, count]) => {
          const searchResult = newSearchResults.find((r) => r.result.id === messageId);
          if (searchResult) {
            searchResult.chatCounter = count;
          }
        });

        this.searchResultsMessages.set(newSearchResults);
      });
  }

  loadMessageFromSearchResult(message: ChatMessage): void {
    const conversation = this.chatConversations().find((c) => c.id === message.conversationId);

    if (this.allMessages()[message.conversationId]?.get(message.id)) {
      this.onSelectedConversationChange(conversation);
      this.openChat();
      this.scrollTo.set(message.id.toString() as ChatMessageId);
    } else {
      const isDifferentConversation = this.selectedConversationId() !== message.conversationId;
      const allMessages = { ...this.allMessages() };
      allMessages[message.conversationId] = new Map();
      this.allMessages.set(allMessages);

      if (isDifferentConversation) {
        this.pendingSearchNavigation.set(message);
        this.onSelectedConversationChange(conversation);
      } else {
        this.store.dispatch(
          runtimeActions.updateElementQueryNode({ elementId: this.elementId, query: undefined }),
        );
        this.navigateToSearchResult(message);
      }
    }
    this.scrollTo.set(message.id.toString() as ChatMessageId);
  }

  private sendMessage(message: string): void {
    this.assertSchemas();

    const selectedConversationId = this.selectedConversationId();
    const isMessageEmpty = !message.length && !this.attachments().length;

    if (this.isSendDisabled() || !selectedConversationId || isMessageEmpty) {
      return;
    }

    const selectedConversationDatasetRow = this.conversationsMap.get(selectedConversationId);

    if (!selectedConversationDatasetRow) {
      return;
    }

    const messagesColumns = this.chatElementService.getMessagesColumns();
    const conversationsColumns = this.chatElementService.getConversationsColumns();

    const datasetRow: DatasetRow = {
      [messagesColumns.conversation]: {
        rowId: selectedConversationDatasetRow[conversationsColumns.rowId],
      },
      [messagesColumns.content]: message,
      [messagesColumns.attachments]: this.attachments().map((file) => file.url),
    };
    const sender = this.chatMembers().find((row) => row.email === this.currentUser()?.email);
    const tempMessage: ChatMessage | undefined = sender
      ? {
          conversationId: selectedConversationId,
          content: message,
          createdAt: new Date(),
          sender,
          updatedAt: new Date(),
          attachments: this.attachments()
            .map((file) => file.url)
            .filter(Boolean) as string[],
          id: `temp_${new Date().getTime()}`, // Temporary ID until the message is saved
        }
      : undefined;

    if (tempMessage) {
      this.messageOptimisticSave(tempMessage);
    }

    this.datasetsApiService
      .postInsert({ datasetId: this.originalSchemas.messages.id, row: datasetRow })
      .subscribe({
        next: (savedMessageDatasetRow) => {
          if (tempMessage) {
            this.removeTemporaryMessageAfterInsert(
              savedMessageDatasetRow,
              tempMessage,
              selectedConversationId,
            );
          }
          this.scrollTo.set('bottom');
          this.attachments.set([]);
          this.handleMessageSentAction(savedMessageDatasetRow);
        },
        error: () => {
          if (tempMessage?.id) {
            this.setErrorForTempMessage(selectedConversationId, tempMessage.id);
          }
        },
      });
  }

  private clearSearch(): void {
    this.showConversationList.set(true);
    this.showSearchResults.set(false);
    this.searchMessagesLoading.set(false);
    this.searchResultsMessages.set([]);
    this.searchResultsChats.set([]);
    this.scrollTo.set('bottom');
  }

  /**
   * This method is used for optimistic update.
   */
  private messageOptimisticSave(message: ChatMessage, isUpdate = false): void {
    const allMessages = { ...this.allMessages() };

    const conversationId = this.selectedConversationId();
    if (!conversationId) {
      return;
    }
    const conversationMessages = allMessages[conversationId] ?? new Map<number, ChatMessage>();
    const newMessage: ChatMessage = isUpdate
      ? {
          ...conversationMessages.get(message.id),
          ...message,
        }
      : message;
    conversationMessages.set(message.id, newMessage);
    allMessages[conversationId] = conversationMessages;

    this.allMessages.set(allMessages);
  }

  private upsertMessages(messages: ChatMessage[]): void {
    const allMessages = { ...this.allMessages() };

    const conversationId = this.selectedConversationId();
    if (conversationId && this.unreadMessagesCount() > MESSAGES_PAGINATION_PAGE_SIZE) {
      allMessages[conversationId]?.clear?.();
    }

    for (const message of this.sortMessages(messages)) {
      const conversationMessages =
        allMessages[message.conversationId] ?? new Map<ChatMessageId, ChatMessage>();

      conversationMessages.set(message.id, message);
      allMessages[message.conversationId] = conversationMessages;
    }

    if (!messages.length && conversationId && !allMessages[conversationId]) {
      this.introConversationIds.update((ids) => {
        if (ids.has(conversationId)) {
          return ids;
        }
        return new Set([...ids, conversationId]);
      });
    }

    this.allMessages.set(allMessages);

    const pendingMsg = this.pendingSearchNavigation();
    if (pendingMsg && pendingMsg.conversationId === conversationId) {
      const conversationMessagesMap = allMessages[conversationId];
      if (conversationMessagesMap && conversationMessagesMap.size > 0 && messages.length > 0) {
        this.pendingSearchNavigation.set(undefined);
        if (conversationMessagesMap.has(pendingMsg.id)) {
          this.openChat();
          this.scrollTo.set(pendingMsg.id.toString() as ChatMessageId);
        } else {
          this.navigateToSearchResult(pendingMsg);
        }
      }
    }
    this.pendingSearchNavigation.set(undefined);
  }

  /**
   * Generates initial messages based on intro configuration.
   * Returns undefined when no intro messages exist so callers skip setting an empty map.
   */
  private generateInitialMessages(conversationId: number): ChatMessage[] | undefined {
    const introMessagesConfig = this.introMessagesConfig();
    if (!introMessagesConfig) {
      return undefined;
    }
    return introMessagesConfig.messages.map(
      (msg, i, array) =>
        ({
          id: -array.length + i,
          content: msg.message,
          createdAt: new Date(),
          updatedAt: new Date(),
          sender: {
            id: 'prop-config',
            name: '',
          },
          conversationId,
        }) as ChatMessage,
    );
  }

  private buildSearchResultQuery(messageId: ChatMessageId): RuntimeEventQueryData {
    return {
      sort: {
        columnName: this.schemaService.getColumnName(
          this.props?.content.input?.messages.createdTime,
        ),
        direction: 'Ascending',
      },
      query: {
        nodeType: QueryNodeType.Logical,
        logicalType: LogicalType.And,
        children: [
          {
            nodeType: QueryNodeType.Comparison,
            value: messageId.toString() ?? '',
            operator: ComparisonOperator.Gte,
            displayInfos: [],
            isOptional: false,
            isFormulaValue: false,
            column: this.chatElementService.getMessagesColumns().id,
          },
        ],
      },
      elementId: this.elementId,
      dataSource: this.props?.data?.find((d) => d?.key === 'messages')?.dataSource,
    };
  }

  private navigateToSearchResult(message: ChatMessage): void {
    const data = this.buildSearchResultQuery(message.id);
    if (this.paginationEmitTracker.shouldEmit(data)) {
      this.output.emit({
        eventType: 'query',
        data,
        isInitialData: false,
        output: 'pagination',
      });
    }
    this.openChat();
    this.scrollTo.set(message.id.toString() as ChatMessageId);
  }

  private loadConversations(): Observable<ChatConversation[]> {
    return this.chatElementService.conversationsColumnsInitialized$.pipe(
      first(Boolean),
      switchMap(() =>
        combineLatest({
          dataInput: this.getDataInputWithProps(this.conversationsDataInput.dataInput$),
          members: this.chatMembers$,
        }),
      ),
      map(({ dataInput, members }) => {
        const map = new Map<number, DatasetRow>();
        const conversations: ChatConversation[] = [];
        const conversationRowIds: string[] = [];
        const conversationRowIdColumn = this.chatElementService.getConversationsColumns().rowId;

        for (const row of dataInput.data) {
          const conversation = this.chatElementService.datasetRowToConversation(
            row,
            members,
            this.currentUser()?.email,
          );
          map.set(conversation.id, row);
          conversations.push(conversation);
          conversationRowIds.push(row[conversationRowIdColumn]);
        }

        this.conversationsMap = map;
        this.listenToMessagesChanges(conversationRowIds);
        return conversations;
      }),
      distinctUntilChanged(isDeepEqual),
    );
  }

  private loadUnreadMessagesData(): Observable<
    Map<number, { unreadMessages: number; lastMessageId: number }>
  > {
    return this.chatElementService.conversationsInfoColumnsInitialized$.pipe(
      first(Boolean),
      switchMap(() => this.getDataInputWithProps(this.conversationsInfoDataInput.dataInput$)),
      map((dataWithProps) => {
        const data = dataWithProps.data;
        const map = new Map<number, { unreadMessages: number; lastMessageId: number }>();
        for (const row of data) {
          const { unreadMessages, conversationId, lastMessageId } =
            this.chatElementService.datasetRowToConversationsInfo(row);
          map.set(conversationId, { unreadMessages, lastMessageId });
        }
        return map;
      }),
    );
  }

  private loadMessages(): Observable<ChatMessage[]> {
    return this.chatElementService.messagesColumnsInitialized$.pipe(
      first(Boolean),
      switchMap(() => this.selectedConversationId$),
      debounceTime(0),
      switchMap(() =>
        combineLatest({
          dataInput: this.getDataInputWithProps(this.messagesDataInput.dataInput$),
          members: this.chatMembers$.pipe(skipWhile((members) => members.length <= 0)),
        }),
      ),
      map(({ dataInput, members }) =>
        dataInput.data.map((row) => this.chatElementService.datasetRowToMessage(row, members)),
      ),
    );
  }

  private getDataInputWithProps(
    dataInput$: Observable<CanvasElementDataInputPayload | undefined>,
  ): Observable<{
    schema: BlazeSchema;
    data: DatasetRow[];
    props: PropsOf<'chat'>;
  }> {
    return combineLatest({
      dataInput: dataInput$,
      props: this.props$,
    }).pipe(
      map(({ dataInput, props }) => ({
        schema: dataInput?.schema,
        data: dataInput?.data ?? [],
        props,
      })),
      filter(
        (
          dataInput,
        ): dataInput is {
          schema: BlazeSchema;
          data: DatasetRow[];
          props: PropsOf<'chat'>;
        } => dataInput.schema && dataInput.data && dataInput.props,
      ),
    );
  }

  private cacheChatColumns(): void {
    this.element$
      .pipe(
        filter(Boolean),
        switchMap((element) => this.chatElementService.cacheColumns(element)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private setSchemaForMessages(): void {
    if (!this.bindingResults?.messages) {
      this.setDefaultEventSchema('action-chat-message-sent');
    }

    const props$ = this.props$.pipe(
      map((props) => props?.content?.input),
      distinctUntilChanged(isDeepEqual),
    );

    combineLatest({
      element: this.element$,
      props: props$,
    })
      .pipe(
        filter(({ element }) => !!element),
        switchMap(({ element }) => this.chatSchemaService.getDatasetSchema$(element, 'messages')),
        filter(Boolean),
        distinctUntilChanged(isDeepEqual),
        untilDestroyed(this),
      )
      .subscribe((schema) => {
        this.setDefaultEventSchema(
          'action-chat-message-sent',
          convertBlazeSchemaToElementSchema(schema, this.elementId, 'action-chat-message-sent'),
        );
      });
  }

  private setSchemaForConversations(): void {
    if (!this.bindingResults?.conversations) {
      this.setDefaultEventSchema('chat-conversation-select');
      this.setDefaultEventSchema('action-chat-conversation-added');
      this.setDefaultEventSchema('action-chat-conversation-edited');
      // this.setDefaultEventSchema('action-chat-conversation-deleted');
      this.setDefaultEventSchema('action-chat-conversation-left');
    }

    this.getDataInput('conversations')
      .schema$.pipe(filter(Boolean), untilDestroyed(this))
      .subscribe((schema) => {
        this.setDefaultEventSchema(
          'chat-conversation-select',
          convertBlazeSchemaToElementSchema(schema, this.elementId, 'chat-conversation-select'),
        );
        this.setDefaultEventSchema(
          'action-chat-conversation-added',
          convertBlazeSchemaToElementSchema(
            schema,
            this.elementId,
            'action-chat-conversation-added',
          ),
        );
        this.setDefaultEventSchema(
          'action-chat-conversation-edited',
          convertBlazeSchemaToElementSchema(
            schema,
            this.elementId,
            'action-chat-conversation-edited',
          ),
        );
        // this.setDefaultEventSchema(
        //   'action-chat-conversation-deleted',
        //   convertBlazeSchemaToElementSchema(schema, this.elementId, 'action-chat-conversation-deleted'),
        // );
        this.setDefaultEventSchema(
          'action-chat-conversation-left',
          convertBlazeSchemaToElementSchema(
            schema,
            this.elementId,
            'action-chat-conversation-left',
          ),
        );
      });
  }

  private setErrorForTempMessage(conversationId: number, optimisticMessageId: ChatMessageId): void {
    const allMessages = { ...this.allMessages() };

    const conversationMessages = allMessages[conversationId] ?? new Map<number, ChatMessage>();
    const message = conversationMessages.get(optimisticMessageId);
    if (!message) {
      return;
    }
    message.errorWhileSending = true;
    conversationMessages.set(message.id, message);
    allMessages[conversationId] = conversationMessages;

    this.allMessages.set(allMessages);
  }

  private createConversation(name: string, members: ChatMember[]): void {
    this.assertSchemas();

    const currentUser = this.currentUser();
    const allMembers = this.chatMembers();

    if (!currentUser) {
      return;
    }

    const columns = this.chatElementService.getConversationsColumns();

    const currentUserMember = allMembers.find((member) => member.email === currentUser.email);

    const membersRowIds = Array.from(
      new Set([...members.map((member) => member.id), currentUserMember?.id]),
    )
      .filter(Boolean)
      .map((rowId) => ({ rowId }));

    const datasetRow = {
      [columns.name]: name,
      [columns.members]: membersRowIds,
    };

    this.isLoading.set(true);
    this.datasetRowsService
      .insert({ datasetId: this.originalSchemas.conversations.id, row: datasetRow })
      .pipe(
        first(),
        tap((createdConversation) => {
          this.handleConversationAddedAction(createdConversation);
          this.createdConversationId.set(
            createdConversation[this.chatElementService.getConversationsColumns().id],
          );
          this.isSendDisabled.set(false);
          this.showChatForm.set(false);
        }),
        exhaustMap((response) => this.createConversationsInfo(response)),
        first(),
      )
      .subscribe(() => {
        this.isLoading.set(false);
      });
  }

  private updateConversation(id: number, name: string, members: ChatMember[]): void {
    this.assertSchemas();
    const currentUser = this.currentUser();
    const allMembers = this.chatMembers();
    const conversation = this.chatConversations().find((c) => c.id === id);

    if (!currentUser || !conversation) {
      return;
    }

    const columns = this.chatElementService.getConversationsColumns();

    const currentUserMember = allMembers.find((member) => member.email === currentUser.email);

    const datasetRow = {
      [columns.id]: conversation.id,
      [columns.name]: name,
      [columns.members]: [
        ...members.map((member) => ({
          rowId: member.id,
        })),
        {
          rowId: currentUserMember?.id,
        },
      ].filter((members) => !!members.rowId),
    };

    const previousDatasetRow = this.conversationsMap.get(conversation.id);

    if (!previousDatasetRow) {
      return;
    }

    const deletedMembers: string[] = [];
    const datasetRowMembers = datasetRow[columns.members] as {
      rowId: string;
    }[];

    for (const member of previousDatasetRow[columns.members]) {
      if (datasetRowMembers.findIndex((drm) => drm.rowId === member.rowId) === -1) {
        deletedMembers.push(member.rowId);
      }
    }

    const newMembers = (
      datasetRow[columns.members] as {
        rowId: string;
      }[]
    ).filter(
      (drm) =>
        previousDatasetRow[columns.members].findIndex(
          (m: { rowId: string }) => m.rowId === drm.rowId,
        ) === -1,
    );

    this.datasetRowsService
      .update(this.originalSchemas.conversations.id, {
        value: datasetRow,
        rowId: previousDatasetRow[columns.rowId],
      })
      .pipe(
        first(),
        tap((updatedConversation) => this.handleConversationUpdatedAction(updatedConversation)),
        exhaustMap(() =>
          newMembers.length > 0
            ? this.createConversationsInfo({
                [columns.rowId]: previousDatasetRow[columns.rowId],
                [columns.members]: newMembers,
              })
            : of(null),
        ),
        exhaustMap(() =>
          deletedMembers.length > 0
            ? this.deleteConversationsInfoForMembers(
                previousDatasetRow[columns.rowId],
                deletedMembers,
              )
            : of(null),
        ),
      )
      .subscribe(() => {
        this.isLoading.set(false);
        this.showChatForm.set(false);
      });
  }

  private getMembersForCreateChat(): Observable<ChatMember[] | undefined> {
    return this.membersDataInput.schema$.pipe(
      switchMap((schema) =>
        schema
          ? this.membersDataInput.data$.pipe(
              filter(Boolean),
              map((data) => this.chatElementService.mapToChatMembers(data)),
            )
          : of(undefined),
      ),
    );
  }

  private getAllChatMembers(): Observable<ChatMember[]> {
    return this.element$.pipe(
      first(Boolean),
      switchMap((element) =>
        this.chatElementService.getChatMembersSchema$(element).pipe(
          distinctUntilChanged(isEqual),
          switchMap((schema) =>
            this.datasetsApiService.postGet({
              datasetId: schema.id,
              filterQuery: {
                children: [],
                logicalType: LogicalType.And,
                nodeType: QueryNodeType.Logical,
              },
            }),
          ),
        ),
      ),
      map((data) => this.chatElementService.mapToChatMembers(data)),
    );
  }

  private getChatMembers(): Observable<{
    create: ChatMember[] | undefined;
    all: ChatMember[];
  }> {
    return this.props$.pipe(
      filter((props) => !!props?.content?.input?.conversationsInfo),
      switchMap(() => this.chatElementService.membersColumnsInitialized$),
      first(Boolean),
      switchMap(() =>
        combineLatest({
          create: this.getMembersForCreateChat(),
          all: this.getAllChatMembers(),
        }),
      ),
    );
  }

  private createConversationsInfo(conversation: DatasetRow): Observable<unknown> {
    this.assertSchemas();

    const conversationsInfoSchema = this.originalSchemas.conversationsInfo;

    const conversationsColumns = this.chatElementService.getConversationsColumns();
    const conversationsInfoColumns = this.chatElementService.getConversationsInfoColumns();

    const rows = conversation[conversationsColumns.members].map((member: { rowId: string }) => ({
      [conversationsInfoColumns.conversation]: {
        rowId: conversation[conversationsColumns.rowId],
      },
      [conversationsInfoColumns.memberLinkColumn]: member,
    }));

    return forkJoin(
      rows.map((row: DatasetRow) =>
        this.datasetRowsService
          .insert({ datasetId: conversationsInfoSchema.id, row })
          .pipe(first()),
      ),
    );
  }

  private deleteConversationsInfoForMembers(
    conversationRowId: string,
    membersRowIds: string[],
  ): Observable<unknown> {
    this.assertSchemas();

    const memberColumn = this.chatElementService.getConversationsInfoColumns().memberLinkColumn;
    const conversationColumn = this.chatElementService.getConversationsInfoColumns().conversation;

    const queries: LogicalPredicateNode[] = membersRowIds.map((member) => ({
      nodeType: PredicateType.Logical,
      logicalType: LogicalType.And,
      children: [
        {
          nodeType: PredicateType.Comparison,
          names: [memberColumn],
          op: ComparisonOperator.Eq,
          value: member,
        },
        {
          nodeType: PredicateType.Comparison,
          names: [conversationColumn],
          op: ComparisonOperator.Eq,
          value: conversationRowId,
        },
      ],
    }));

    return this.datasetRowsService
      .deleteBatch({ datasetId: this.originalSchemas.conversationsInfo.id, queries })
      .pipe(first());
  }

  private removeUserFromConversation(conversationId: number, email: string): Observable<unknown> {
    return this.chatMembers$.pipe(
      map((members) => members.find((member) => member.email === email)),
      first(Boolean),
      map((member) => {
        this.assertSchemas();
        const membersColumn = this.chatElementService.getConversationsColumns().members;
        const idColumn = this.chatElementService.getConversationsColumns().rowId;
        const conversation = deepClone(this.conversationsMap.get(conversationId));

        if (conversation && conversation[membersColumn]) {
          conversation[membersColumn] = conversation[membersColumn].filter(
            (m: { rowId: string }) => m.rowId !== member.id,
          );

          return {
            conversation: {
              datasetId: this.originalSchemas.conversations.id,
              datasetRow: conversation,
              rowId: conversation[idColumn],
              operation: conversation[membersColumn].length === 0 ? 'DELETE' : 'UPDATE',
            },
            conversationsInfo: {
              datasetId: this.originalSchemas.conversationsInfo.id,
            },
          };
        }

        return {
          conversationsInfo: {
            id: this.originalSchemas.conversationsInfo.id,
          },
        };
      }),
      exhaustMap((data) => {
        if (!data.conversation) {
          return [];
        }

        const requests$: Observable<any>[] = [
          this.datasetRowsService.delete(data.conversationsInfo.datasetId, {
            queryNode: {
              nodeType: PredicateType.Logical,
              logicalType: LogicalType.And,
              children: [
                {
                  nodeType: PredicateType.Comparison,
                  names: [this.chatElementService.getConversationsInfoColumns().conversation],
                  value: data.conversation.rowId,
                  op: ComparisonOperator.Eq,
                },
                {
                  nodeType: PredicateType.Comparison,
                  names: [this.chatElementService.getConversationsInfoColumns().membersEmail],
                  value: email,
                  op: ComparisonOperator.Eq,
                },
              ],
            },
          }),
        ];

        if (data.conversation.operation === 'UPDATE') {
          requests$.push(
            this.datasetRowsService.update(data.conversation.datasetId, {
              value: data.conversation.datasetRow,
              rowId: data.conversation.rowId,
            }),
          );
        } else if (data.conversation.operation === 'DELETE') {
          requests$.push(
            this.datasetRowsService.delete(data.conversation.datasetId, {
              rowId: data.conversation.rowId,
            }),
          );
        }

        return requests$.length ? forkJoin(requests$) : of();
      }),
    );
  }

  private listenToMessagesChanges(conversationRowIds: string[]): void {
    this.messagesChangesSubscription.unsubscribe();
    this.messagesChangesSubscription = this.element$
      .pipe(
        first((element) => !!getActiveBinding(element.props?.data, 'messages')),
        switchMap((element) => this.chatSchemaService.getDatasetSchema$(element, 'messages')),
        first(Boolean),
        switchMap((schema: BlazeSchema) =>
          this.subscriptionService
            .subscribe(schema.id, SubscriptionAction.Count, {
              logicalType: LogicalType.And,
              nodeType: QueryNodeType.Logical,
              children: [
                {
                  nodeType: QueryNodeType.Comparison,
                  value: `ARRAY(${conversationRowIds.map((c) => `"${c}"`).join(', ')})`,
                  operator: ComparisonOperator.In,
                  column: this.chatElementService.getMessagesColumns().conversation,
                  isFormulaValue: true,
                  isOptional: false,
                  ignoreIfEmptyValue: false,
                  displayInfos: [],
                },
              ],
            })
            .pipe(
              map(() => ({
                tableId: schema.id,
              })),
            ),
        ),
        skip(1),
        throttleTime(MESSAGES_REFRESH_THROTTLE_TIME_MS),
      )
      .subscribe(({ tableId }) => {
        this.datasetRowsService.notifyDatasetsChanged([tableId]);
        this.scrollTo.set('bottom');
      });
  }

  private listenToConversationsChanges(query: LogicalQueryNode | undefined): void {
    this.element$
      .pipe(
        first((element) => !!getActiveBinding(element.props?.data, 'conversations')),
        switchMap((element) => this.chatSchemaService.getDatasetSchema$(element, 'conversations')),
        first(Boolean),
        switchMap((schema: BlazeSchema) =>
          this.subscriptionService.subscribe(schema.id, SubscriptionAction.Count, query).pipe(
            distinctUntilChanged(),
            map(() => ({
              tableId: schema.id,
            })),
          ),
        ),
        skip(1),
        throttleTime(CONVERSATIONS_REFRESH_THROTTLE_TIME_MS),
      )
      .subscribe(({ tableId }) => {
        this.datasetRowsService.notifyDatasetsChanged([tableId]);
      });
  }

  private assignStatusesColors(schema: BlazeSchema): void {
    this.statusColorMap.set(
      Object.fromEntries(
        findColumn(
          schema.columns,
          this.chatElementService.getMembersColumns().status,
        )?.tagDefinition?.items?.map(({ value, backgroundColor }) => [value, backgroundColor]) ??
          [],
      ),
    );
  }

  private listenToChatMembersStatusChanges(query: LogicalQueryNode | undefined): void {
    this.element$
      .pipe(
        first((element) => !!getActiveBinding(element.props?.data, 'members')),
        switchMap((element) => this.chatSchemaService.getDatasetSchema$(element, 'members')),
        first(Boolean),
        tap((schema) => this.assignStatusesColors(schema)),
        switchMap((schema: BlazeSchema) =>
          this.subscriptionService.subscribe(schema.id, SubscriptionAction.Update, query).pipe(
            distinctUntilChanged(),
            map(() => ({
              tableId: schema.id,
            })),
          ),
        ),
        skip(1),
        throttleTime(MEMBERS_STATUS_REFRESH_THROTTLE_TIME_MS),
      )
      .subscribe(({ tableId }) => {
        this.datasetRowsService.notifyDatasetsChanged([tableId]);
      });
  }

  private listenToQueryStateChanges(): void {
    this.queryState$?.pipe(untilDestroyed(this)).subscribe((state) => {
      this.paginationProps = this.elementPaginationService.applyPaginationStateToModel(
        this.paginationProps,
        state,
      );
      this.allMessagesLoaded.set(!this.paginationProps.state.hasNextPage);
    });
  }

  private loadBlazeTableSchemas(): void {
    const props$ = this.props$.pipe(
      map((props) => props?.content?.input),
      distinctUntilChanged(isDeepEqual),
    );

    this.isPending$
      .pipe(
        switchMap(() =>
          combineLatest({
            props: props$,
            element: this.element$.pipe(first(Boolean)),
          }),
        ),
        switchMap(({ element }) =>
          combineLatest({
            conversations: this.chatSchemaService
              .getDatasetSchema$(element, 'conversations')
              .pipe(filter(Boolean)),
            messages: this.chatSchemaService
              .getDatasetSchema$(element, 'messages')
              .pipe(filter(Boolean)),
            conversationsInfo: this.chatSchemaService
              .getDatasetSchema$(element, 'conversationsInfo')
              .pipe(filter(Boolean)),
            members: this.chatElementService.getChatMembersSchema$(element).pipe(filter(Boolean)),
          }),
        ),
        untilDestroyed(this),
      )
      .subscribe((schemas) => {
        this.originalSchemas = schemas;
        this.chatSchemasLoaded.set(true);
        this.membersTableName.set(schemas.members.name);
      });
  }

  private handleMessageSentAction(dataRow: DatasetRow): void {
    const dataSource = getActiveBinding(this.props?.data, 'messages')?.dataSource;
    if (
      !this.props?.content.actions?.sendMessage?.enabled ||
      !this.props.content.actions.sendMessage.targetId ||
      !dataSource
    ) {
      return;
    }

    this.assertSchemas();
    this.handleChatAction(
      {
        id: ITEMS_CONTAINER_ACTION_SEND_MESSAGE,
        targetId: this.props.content.actions.sendMessage.targetId,
      },
      dataSource,
      dataRow,
      dataRow[this.chatElementService.getMessagesColumns().rowId],
      convertBlazeSchemaToElementSchema(
        this.originalSchemas.messages,
        this.elementId,
        'action-chat-message-sent',
      ),
    );
  }

  private handleConversationAddedAction(dataRow: DatasetRow): void {
    const dataSource = getActiveBinding(this.props?.data, 'conversations')?.dataSource;
    if (
      !this.props?.content.actions?.addConversation?.enabled ||
      !this.props.content.actions.addConversation.targetId ||
      !dataSource
    ) {
      return;
    }

    this.assertSchemas();
    this.handleChatAction(
      {
        id: ITEMS_CONTAINER_ACTION_ADD_CONVERSATION,
        targetId: this.props.content.actions.addConversation.targetId,
      },
      dataSource,
      dataRow,
      dataRow[this.chatElementService.getConversationsColumns().rowId],
      convertBlazeSchemaToElementSchema(
        this.originalSchemas.conversations,
        this.elementId,
        'action-chat-conversation-added',
      ),
    );
  }

  private handleConversationUpdatedAction(dataRow: DatasetRow): void {
    const dataSource = getActiveBinding(this.props?.data, 'conversations')?.dataSource;
    if (
      !this.props?.content.actions?.editConversation?.enabled ||
      !this.props.content.actions.editConversation.targetId ||
      !dataSource
    ) {
      return;
    }

    this.assertSchemas();
    this.handleChatAction(
      {
        id: ITEMS_CONTAINER_ACTION_EDIT_CONVERSATION,
        targetId: this.props.content.actions.editConversation.targetId,
      },
      dataSource,
      dataRow,
      dataRow[this.chatElementService.getConversationsColumns().rowId],
      convertBlazeSchemaToElementSchema(
        this.originalSchemas.conversations,
        this.elementId,
        'action-chat-conversation-edited',
      ),
    );
  }

  private handleConversationLeftAction(dataRow: DatasetRow): void {
    const dataSource = getActiveBinding(this.props?.data, 'conversations')?.dataSource;
    if (
      !this.props?.content.actions?.leaveConversation?.enabled ||
      !this.props.content.actions.leaveConversation.targetId ||
      !dataSource
    ) {
      return;
    }

    this.assertSchemas();
    this.handleChatAction(
      {
        id: ITEMS_CONTAINER_ACTION_LEAVE_CONVERSATION,
        targetId: this.props.content.actions.leaveConversation.targetId,
      },
      dataSource,
      dataRow,
      dataRow[this.chatElementService.getConversationsColumns().rowId],
      convertBlazeSchemaToElementSchema(
        this.originalSchemas.conversations,
        this.elementId,
        'action-chat-conversation-left',
      ),
    );
  }

  private handleChatAction(
    action: {
      id: ItemContainerActionsIds;
      targetId: string;
    },
    dataSource: DataBindingSourceInfo,
    dataRow: DatasetRow,
    dataRowId: string,
    dataRowSchema: ElementSchema,
  ): void {
    this.containerActionsService.handleListlikeAction({
      dataRow,
      dataRowId,
      dataRowSchema,
      output: this.output,
      action: {
        ...action,
        targetType: 'Workflow',
      } as any,
      props: this.props as any,
      dataSource,
    });
  }

  private assertSchemas(): asserts this is {
    originalSchemas: {
      messages: BlazeSchema;
      conversations: BlazeSchema;
      conversationsInfo: BlazeSchema;
      members: BlazeSchema;
    };
  } {
    if (!this.originalSchemas || Object.values(this.originalSchemas).some((schema) => !schema)) {
      throw new Error('Schemas not initialized');
    }
  }

  private updateStyles(): void {
    const { general, participants, messages, titleBlock } = this.props?.style ?? {};

    if (!general || !participants || !messages || !titleBlock) {
      return;
    }

    this.titleColor = general.title.color1;
    this.backgroundColor = general.background.color1;
    this.outlineColor = general.outline.color1;
    this.accentColor = general.accent.color1;
    this.accentLabelColor = general.accentLabel.color1;
    this.radius = general.radius;

    this.participantsLabelColor = participants.label.color1;
    this.participantsHoverBackgroundColor = participants.hoverBackground.color1;
    this.participantsSelectedBackgroundColor = participants.selectedBackground.color1;

    this.messagesMyTextColor = messages.myText.color1;
    this.messagesMyBackgroundColor = messages.myBackground.color1;
    this.messagesOthersTextColor = messages.othersText.color1;
    this.messagesOthersBackgroundColor = messages.othersBackground.color1;
    this.messagesFontFamily = messages.fontFamily || undefined;
    this.messagesFontWeight = this.resolveFontWeight(messages.fontFamily, messages.fontWeight);
    this.messagesFontSize = messages.fontSize || undefined;
    this.messagesLineHeight = messages.lineHeight || undefined;

    const input = this.props?.style?.input;
    this.inputTextColor = input?.text?.color1 ?? this.inputTextColor;
    this.inputBackgroundColor = resolveOptionalColor(input?.background);
    this.inputOutlineColor = resolveOptionalColor(input?.outline);
    this.inputFontFamily = input?.fontFamily || undefined;
    this.inputFontWeight = this.resolveFontWeight(input?.fontFamily, input?.fontWeight);
    this.inputFontSize = input?.fontSize || undefined;
    this.inputLineHeight = input?.lineHeight || undefined;

    this.boxShadow =
      makeContainerVisualEffectStyles(general.visualEffect)?.['box-shadow'] ?? 'none';
    this.disabledActionBackgroundColor = participants.hoverBackground.color1;
    this.disabledActionColor = general.outline.color1;
  }

  private resolveFontWeight(fontFamily?: string, fontWeight?: string): string | undefined {
    return fontFamily
      ? this.fontsService.getFontWeight(fontFamily, fontWeight)
      : fontWeight || undefined;
  }

  private assignColors(): void {
    combineLatest({
      activeTheme: this.themeService.activeTheme$,
      chatMembers: this.chatMembers$,
      conversations: this.chatConversations$,
    })
      .pipe(untilDestroyed(this))
      .subscribe(({ activeTheme, chatMembers, conversations }) => {
        const theme = {
          tagsPalette: {
            paletteName: 'palette2',
            foreground: activeTheme?.value.palettes?.['palette2']?.fg,
            background: activeTheme?.value.palettes?.['palette2']?.bg || [],
          },
        };
        const chatColors =
          this.elementTagsService.mapValuesToColorPalette(
            chatMembers.map((member) => member.email),
            undefined,
            undefined,
            theme.tagsPalette as any,
            undefined,
            true,
          )?.valuesMap ?? {};
        const conversationsColors =
          this.elementTagsService.mapValuesToColorPalette(
            conversations.map((member) => member.id),
            undefined,
            undefined,
            theme.tagsPalette as any,
            undefined,
            true,
          )?.valuesMap ?? {};

        this.colorsMap.set({ ...chatColors, ...conversationsColors });
      });
  }

  /**
   * Sorts chat messages by their ID.
   * Messages with string IDs (temporary messages) are sorted to the end.
   * Regular messages are sorted by their numeric ID.
   *
   * @param messages The chat messages to sort.
   * @returns The sorted chat messages.
   */
  private sortMessages(messages: ChatMessage[]): ChatMessage[] {
    return messages.sort((a, b) => {
      if (typeof a.id === 'string' && typeof b.id === 'string') {
        return a.id.localeCompare(b.id);
      }

      if (typeof a.id === 'string') {
        return 1; // Temporary messages go to the end
      }

      if (typeof b.id === 'string') {
        return -1; // Temporary messages go to the end
      }

      return a.id - b.id; // Regular messages are sorted by ID
    });
  }

  private listenToDataInputBindingQueryChanges(
    inputBindingKey: 'conversations' | 'members',
  ): Observable<LogicalQueryNode | undefined> {
    return this.props$.pipe(
      map((props) => getActiveBinding(props?.data, inputBindingKey)?.dataSource),
      filter((dataSource) => dataSource?.sourceType === 'context'),
      map((dataSource) => dataSource?.id),
      filter(Boolean),
      switchMap((workflowId) => this.store.select(runtimeSelectors.selectWorkflowById(workflowId))),
      map((workflow) => findFirstNodeInWorkflowOfType<GetWorkflowNode>(workflow, isGetNode)),
      map((node) => node?.query),
      distinctUntilChanged(isDeepEqual),
    );
  }

  private updateConversationsSortWeights(
    unreadMessagesDataEntries: [
      number,
      {
        unreadMessages: number;
        lastMessageId: number;
      },
    ][],
  ): void {
    const unreadCounts = this.allConversationsUnreadMessagesCount();
    const selectedConversationId = this.selectedConversationId();

    this.conversationsSortWeights.update((currentPrioritiesMap) => {
      const newMap = new Map(currentPrioritiesMap);

      unreadMessagesDataEntries.forEach(([conversationId, data]) => {
        const currentCount = data.unreadMessages;
        const previousCount = unreadCounts.get(conversationId) || 0;

        if (this.conversationsSortWeightsInitialized) {
          if (currentCount > previousCount && selectedConversationId !== conversationId) {
            newMap.set(conversationId, Date.now());
          }
        } else {
          newMap.set(conversationId, currentCount > 0 ? 1 : 0);
        }
      });

      return newMap;
    });

    this.conversationsSortWeightsInitialized = true;
  }

  private getSearchResults(searchText: string): Observable<ChatMessage[]> {
    const conversationsIds = this.conversationsMap.keys();
    if (!conversationsIds) {
      return of([]);
    }
    return this.element$.pipe(
      first(Boolean),
      map((element) => ({
        conversationsId: this.chatSchemaService.getBlazeTableId(element, 'conversations'),
        messagesId: this.chatSchemaService.getBlazeTableId(element, 'messages'),
      })),
      switchMap(({ conversationsId, messagesId }) => {
        if (!conversationsId || !messagesId) {
          return of([]);
        }
        return this.datasetsApiService
          .postGet({
            datasetId: conversationsId,
            filterQuery: {
              children: [
                {
                  column: this.chatElementService.getConversationsColumns().id,
                  displayInfos: [],
                  isFormulaValue: false,
                  isOptional: false,
                  nodeType: QueryNodeType.Comparison,
                  operator: ComparisonOperator.In,
                  value: JSON.stringify(Array.from(conversationsIds) ?? []),
                },
              ],
              logicalType: LogicalType.And,
              nodeType: QueryNodeType.Logical,
            },
          })
          .pipe(
            map((conversationsData) =>
              conversationsData.map(
                (r) => r[this.chatElementService.getConversationsColumns().rowId],
              ),
            ),
            switchMap((conversationsRowIds) => {
              return this.datasetsApiService.postGet({
                datasetId: messagesId,
                filterQuery: {
                  children: [
                    {
                      column: this.chatElementService.getMessagesColumns().content,
                      displayInfos: [],
                      isFormulaValue: false,
                      isOptional: false,
                      nodeType: QueryNodeType.Comparison,
                      operator: ComparisonOperator.ContainsText,
                      value: searchText,
                    },
                    {
                      column: this.chatElementService.getMessagesColumns().conversation,
                      displayInfos: [],
                      isFormulaValue: false,
                      isOptional: false,
                      nodeType: QueryNodeType.Comparison,
                      operator: ComparisonOperator.In,
                      value: JSON.stringify(conversationsRowIds ?? []),
                    },
                  ],
                  logicalType: LogicalType.And,
                  nodeType: QueryNodeType.Logical,
                },
              });
            }),
            map((messagesData) =>
              messagesData.map((row) =>
                this.chatElementService.datasetRowToMessage(row, this.chatMembers()),
              ),
            ),
          );
      }),
    );
  }

  private removeTemporaryMessageAfterInsert(
    savedMessageDatasetRow: DatasetRow,
    tempMessage: ChatMessage,
    selectedConversationId: number,
  ): void {
    // The insert echo may not carry the server-filled sender column in a member-matchable
    // shape, and this path only runs for a message just sent by the current user, so the
    // sender identity is taken from the optimistic message instead of the echoed row.
    const savedMessage: ChatMessage = {
      ...this.chatElementService.datasetRowToMessage(savedMessageDatasetRow, this.chatMembers()),
      sender: tempMessage.sender,
    };
    const savedMessageId = savedMessage.id;

    const allMessages = this.allMessages();
    const conversationMessages = allMessages[selectedConversationId];

    if (!conversationMessages) {
      return;
    }

    // Very unlikely scenario - Websocket event arrived before the rest call (/insert) finished
    // so the real message is already in the list, so we just clean up the temporary one
    if (conversationMessages.has(savedMessageId)) {
      conversationMessages.delete(tempMessage.id);
      this.allMessages.set({ ...allMessages });
    } else if (conversationMessages.has(tempMessage.id)) {
      // standard flow - the temp message was added and needs to be replaced with the real message return from the rest call
      conversationMessages.delete(tempMessage.id);
      conversationMessages.set(savedMessageId, savedMessage);
      this.allMessages.set({ ...allMessages });
    }
  }
}

// 'none' maps to undefined so the host CSS variable is removed and SCSS fallbacks apply
function resolveOptionalColor(model?: PropertiesConfigInputColorModel): string | undefined {
  return model && model.selectorType !== 'none' ? model.color1 : undefined;
}
